import {
  ALL_PERMISSIONS,
  PERMISSION_GROUPS,
  PERMISSION_META,
  Permission,
  SystemRoleKey,
  expandPermissions,
  isPermission,
  sanitizePermissions,
  sortPermissions,
  type PermissionGroup,
  type PermissionMeta,
} from '@bookora/shared';

import { isApiError } from '@bookora/ui';

import type { PermissionGroupResponse } from '@/api/generated/model';

import type { StaffMember } from './api';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE ROLE EDITOR'S RULES, AS PURE FUNCTIONS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Everything in this file is a decision the role editor makes about a set of
 * permission keys. None of it touches React, so each rule can be asserted
 * directly — which matters, because these are the rules that decide who may
 * open a patient file.
 *
 * ── WHERE THE LABELS COME FROM ──────────────────────────────────────────────
 *
 * `PERMISSION_GROUPS` in `@bookora/shared` — the same constant the backend's
 * `PermissionsGuard` compiles against and the same one `GET /roles/permissions`
 * serialises. The panel renders from the shared constant rather than from the
 * endpoint on purpose: a label is a compile-time fact, and a screen that waits
 * for the network to learn what «حذف بیمار» means shows an empty checkbox list
 * for the first 300ms of the highest-stakes screen in the product.
 *
 * The endpoint is still fetched, for exactly one reason: {@link catalogueDrift}
 * compares it with the bundle. A panel built against an older `@bookora/shared`
 * than the API is running would otherwise silently hide a permission that
 * exists, and hiding a permission is how a role quietly stops being what its
 * owner thinks it is.
 *
 * ── WHAT THIS FILE IS NOT ───────────────────────────────────────────────────
 *
 * It is not enforcement. Every rule here also has to hold on the server, and
 * where the server does not hold it yet that is written down at the rule.
 */

export const OWNER_ROLE_KEY: string = SystemRoleKey.CLINIC_OWNER;

/** The groups, in registry order, exactly as the shared package defines them. */
export const EDITOR_GROUPS: readonly PermissionGroup[] = PERMISSION_GROUPS;

export function metaOf(permission: Permission): PermissionMeta {
  return PERMISSION_META[permission];
}

/* -------------------------------------------------------------------------- */
/* Prerequisites, both directions                                             */
/* -------------------------------------------------------------------------- */

/**
 * `permission -> the permissions that are meaningless without it`.
 *
 * The forward direction (`requires`) is in the shared registry. The reverse is
 * derived here and is what makes UNTICKING coherent: dropping `patient.view`
 * while `patient.edit` stays ticked stores a role that passes the write guard
 * and then 403s on the read the edit screen does first — the server's
 * `expandPermissions` would put `patient.view` straight back, so the ticked
 * state and the saved state would disagree.
 */
const DEPENDENTS: ReadonlyMap<Permission, readonly Permission[]> = (() => {
  const map = new Map<Permission, Permission[]>();
  for (const permission of ALL_PERMISSIONS) map.set(permission, []);
  for (const permission of ALL_PERMISSIONS) {
    for (const required of PERMISSION_META[permission].requires) {
      map.get(required)?.push(permission);
    }
  }
  return map;
})();

/** Adds `permission` and every prerequisite it drags in. */
export function grant(
  selected: Iterable<Permission>,
  permission: Permission,
): { next: Permission[]; pulledIn: Permission[] } {
  const before = new Set(selected);
  const next = expandPermissions([...before, permission]);
  return { next, pulledIn: next.filter((key) => key !== permission && !before.has(key)) };
}

/** Removes `permission` and, transitively, everything that requires it. */
export function revoke(
  selected: Iterable<Permission>,
  permission: Permission,
): { next: Permission[]; alsoRemoved: Permission[] } {
  const remaining = new Set(selected);
  const removed = new Set<Permission>();

  const walk = (key: Permission): void => {
    if (removed.has(key)) return;
    removed.add(key);
    remaining.delete(key);
    for (const dependent of DEPENDENTS.get(key) ?? []) {
      if (remaining.has(dependent)) walk(dependent);
    }
  };
  walk(permission);

  return {
    next: sortPermissions(remaining),
    alsoRemoved: sortPermissions(removed).filter((key) => key !== permission),
  };
}

/**
 * Ticking a whole group: every key in it, plus the prerequisites they need.
 *
 * ── WHY THE CEILING IS APPLIED AFTER EXPANSION, NOT BEFORE ──────────────────
 *
 * Filtering the group's own keys first and expanding afterwards looks
 * equivalent and is not: `expandPermissions` pulls in PREREQUISITES, and a
 * prerequisite can be a key the actor does not hold. «ثبت مصرف» requires
 * «مشاهده موجودی»; an actor holding the first and not the second would tick the
 * group, watch the panel add both, and collect a 403 from
 * `GrantCeilingService` naming a key they never chose.
 *
 * So each key is added the way a single checkbox adds it — expand, then check
 * the whole delta — and one that drags in something ungrantable is skipped
 * entirely rather than half-applied. The result is always a set the server will
 * accept, which is the only useful definition of correct here.
 */
export function grantGroup(
  selected: Iterable<Permission>,
  group: PermissionGroup,
  rules: GrantabilityRules,
): Permission[] {
  let current = sortPermissions(selected);

  for (const item of group.permissions) {
    if (current.includes(item.key)) continue;
    const candidate = expandPermissions([...current, item.key]);
    if (escalatingKeys(rules, current, candidate).length > 0) continue;
    current = candidate;
  }

  return current;
}

export function revokeGroup(selected: Iterable<Permission>, group: PermissionGroup): Permission[] {
  let current = [...selected];
  for (const item of group.permissions) {
    if (current.includes(item.key)) current = revoke(current, item.key).next;
  }
  return sortPermissions(current);
}

/* -------------------------------------------------------------------------- */
/* Privilege escalation                                                       */
/* -------------------------------------------------------------------------- */

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE GRANT CEILING, MIRRORED
 * ════════════════════════════════════════════════════════════════════════════
 *
 * The server enforces this in `backend/src/auth/permissions/grant-ceiling.service.ts`
 * and refuses with `PERMISSION_GRANT_EXCEEDS_OWN` (403). Everything below
 * exists so the panel does not DRAW something that would then be refused —
 * and, just as importantly, does not FORBID something the server would allow.
 * A UI stricter than the server is the same class of defect as a UI laxer than
 * it: both teach the user that the rule is not what it says.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 *
 * A permission write is a pair of EFFECTIVE sets, `before` and `after`. The
 * actor must hold every key in `after \ before`. Therefore:
 *
 *   · adding a key the actor lacks is refused — that is the escalation;
 *   · removing one always passes; de-escalation cannot raise anybody;
 *   · **a key in both sets is not a grant and is not checked.**
 *
 * That third clause is the one that makes the rule usable, and the one this
 * file used to get wrong. Without it, a role that outranks its editor becomes
 * uneditable by anyone but the owner — an office manager could not fix a typo
 * in «حسابدار ارشد» because the role carries finance keys they do not hold.
 *
 * ── THE OWNER IS EXEMPT, EXPLICITLY ─────────────────────────────────────────
 *
 * In practice the owner preset is the whole registry, so the delta rule never
 * refuses them anyway — but «in practice» is not a guarantee: one `revoked`
 * override would otherwise lock an owner out of administering their own clinic.
 * The server exempts them by `role.key === 'clinic_owner'` on their own active
 * staff row, so this does too. There is no `Staff.isOwner` COLUMN; the
 * `isOwner` field on a response is derived from that key by `staff.presenter.ts`.
 */
export interface GrantabilityRules {
  /** Keys the signed-in user holds — their own effective set, from `/auth/me`. */
  actorPermissions: readonly Permission[];
  /**
   * The actor's own membership is the `clinic_owner` preset. Exempt from the
   * ceiling entirely, matching `GrantCeilingService.actorIsClinicOwner()`.
   */
  isOwner?: boolean;
}

/**
 * The keys `after` adds that the actor cannot hand out.
 *
 * The client-side twin of `escalatingKeys` in the grant-ceiling service, down
 * to dropping unknown keys rather than reporting them: a key retired from the
 * registry but still stored on an old role must not become an unremovable
 * escalation.
 */
export function escalatingKeys(
  rules: GrantabilityRules,
  before: readonly string[],
  after: readonly string[],
): Permission[] {
  if (rules.isOwner) return [];

  const held = new Set<string>(rules.actorPermissions);
  const had = new Set<string>(sanitizePermissions(before));

  return sortPermissions(
    sanitizePermissions(after).filter((key) => !had.has(key) && !held.has(key)),
  );
}

/**
 * Whether a single checkbox may be ticked, given what the subject can ALREADY
 * do.
 *
 * This is the delta rule expressed per key, and `baseline` is what makes it
 * agree with the server: a permission the role or the staff member already has
 * is in both sets, so re-ticking it after an accidental untick is not a grant
 * and must not be locked. A permission that is NOT in the baseline is one this
 * write would add, so the actor has to hold it.
 *
 * For a staff member the baseline is their EFFECTIVE set (role + grants −
 * revocations), which is why clearing a `revoked` entry for a key the role
 * carries counts as a grant: the key is not in `before`.
 */
export function grantabilityFor(
  rules: GrantabilityRules,
  baseline: Iterable<string>,
): (permission: Permission) => boolean {
  if (rules.isOwner) return () => true;

  const held = new Set<string>(rules.actorPermissions);
  const had = new Set<string>(sanitizePermissions([...baseline]));

  return (permission) => held.has(permission) || had.has(permission);
}

/**
 * Whether the actor may hand out `permission` from nothing — the shape the
 * server uses for an INVITE and for a starter kit's new roles, where `before`
 * is empty because the membership or the role does not exist yet.
 */
export function canGrant(rules: GrantabilityRules, permission: Permission): boolean {
  return rules.isOwner === true || rules.actorPermissions.includes(permission);
}

/** Every key of a not-yet-existing role the actor could not hand out. */
export function ungrantableFromNothing(
  rules: GrantabilityRules,
  wanted: readonly string[],
): Permission[] {
  return escalatingKeys(rules, [], wanted);
}

/* -------------------------------------------------------------------------- */
/* Diffing, for the "are you sure" step                                       */
/* -------------------------------------------------------------------------- */

export interface PermissionDiff {
  added: Permission[];
  removed: Permission[];
  /** Removed keys that were exposing PHI, imagery or money — worth naming. */
  removedSensitive: Permission[];
  addedSensitive: Permission[];
  changed: boolean;
}

export function diffPermissions(
  before: readonly string[],
  after: readonly string[],
): PermissionDiff {
  const from = new Set(sanitizePermissions(before));
  const to = new Set(sanitizePermissions(after));

  const added = sortPermissions([...to].filter((key) => !from.has(key)));
  const removed = sortPermissions([...from].filter((key) => !to.has(key)));

  return {
    added,
    removed,
    addedSensitive: added.filter((key) => PERMISSION_META[key].sensitive),
    removedSensitive: removed.filter((key) => PERMISSION_META[key].sensitive),
    changed: added.length > 0 || removed.length > 0,
  };
}

/* -------------------------------------------------------------------------- */
/* Owner protection                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Owner-grade membership, decided the way the server decides it.
 *
 * `role.key === 'clinic_owner'` and nothing else. There is no `Staff.isOwner`
 * column — `StaffResponse.isOwner` is derived from exactly this comparison in
 * `staff.presenter.ts`, and `GrantCeilingService.actorIsClinicOwner()` and
 * `StaffService.assertNotLastOwner` both read the key. Treating the derived
 * boolean as a second, independent source would let the two disagree the day a
 * response is built by hand in a test or a fixture.
 */
export function isOwnerStaff(member: StaffMember): boolean {
  return member.role.key === OWNER_ROLE_KEY;
}

export function activeOwners(staff: readonly StaffMember[]): StaffMember[] {
  return staff.filter((member) => member.isActive && isOwnerStaff(member));
}

/**
 * The one state a clinic cannot recover from on its own.
 *
 * Billing, staff, roles and settings all sit behind keys only the owner preset
 * holds, so a clinic with no active owner needs a super-admin to get back in.
 * The server refuses this too (`STAFF_IS_LAST_OWNER` on assign-role,
 * deactivate and remove) — this is the affordance that stops a user walking
 * into the refusal, not the thing preventing it.
 */
export function isLastOwner(member: StaffMember, staff: readonly StaffMember[]): boolean {
  if (!member.isActive || !isOwnerStaff(member)) return false;
  return activeOwners(staff).length <= 1;
}

/**
 * The one key the last owner may not be stripped of.
 *
 * `StaffService.assertOwnerKeepsAdministration` refuses a `revoked` list
 * containing `staff.manage_roles` when the target is the last active owner —
 * that is the delta between "a clinic with an owner" and "a clinic whose owner
 * cannot appoint anybody", which is the same dead end by a slower route.
 *
 * Note how NARROW this is, and match it: every other permission may be revoked
 * from the last owner, and the panel used to lock the whole editor for them.
 * Forbidding what the server allows is its own defect — it teaches an owner
 * that trimming their own account is impossible when only one checkbox is.
 */
export function isLockedForLastOwner(member: StaffMember, staff: readonly StaffMember[]) {
  const locked = isLastOwner(member, staff);
  return {
    /** Moving them off the owner role, deactivating or removing them. */
    membership: locked,
    /** `staff.manage_roles` specifically — nothing else. */
    permission: (permission: Permission): boolean =>
      locked && permission === Permission.STAFF_MANAGE_ROLES,
  };
}

/* -------------------------------------------------------------------------- */
/* Drift between the bundle and the running API                               */
/* -------------------------------------------------------------------------- */

export interface CatalogueDrift {
  /** In the API's registry but not in this build — cannot be granted here. */
  unknownToPanel: string[];
  /** In this build but not in the API's — would be rejected on save. */
  unknownToServer: string[];
  inSync: boolean;
}

export function catalogueDrift(
  serverGroups: readonly PermissionGroupResponse[] | undefined,
): CatalogueDrift {
  if (!serverGroups) return { unknownToPanel: [], unknownToServer: [], inSync: true };

  const server = new Set(serverGroups.flatMap((group) => group.permissions.map((p) => p.key)));
  const panel = new Set<string>(ALL_PERMISSIONS);

  const unknownToPanel = [...server].filter((key) => !panel.has(key)).sort();
  const unknownToServer = [...panel].filter((key) => !server.has(key)).sort();

  return {
    unknownToPanel,
    unknownToServer,
    inSync: unknownToPanel.length === 0 && unknownToServer.length === 0,
  };
}

/* -------------------------------------------------------------------------- */
/* The server's refusal, read back                                            */
/* -------------------------------------------------------------------------- */

export interface GrantRefusal {
  /** Up to ten keys the server named, already resolved to Persian labels. */
  labels: string[];
  /** The true count, which may exceed `labels.length`. */
  count: number;
}

/**
 * Reads `PERMISSION_GRANT_EXCEEDS_OWN`'s `details` into something showable.
 *
 * The server sends `{ ungrantable: string[], count: number }` precisely so the
 * role editor can point at the offending checkboxes instead of showing a bare
 * refusal on a screen with sixty of them. The list is capped at ten with the
 * real size alongside — say «و ۴ مورد دیگه» rather than implying there were
 * only ten.
 *
 * Returns `null` for any other failure, so a caller can fall through to the
 * ordinary Persian toast.
 */
export function readGrantRefusal(error: unknown): GrantRefusal | null {
  if (!isApiError(error) || error.code !== 'PERMISSION_GRANT_EXCEEDS_OWN') return null;

  const details = error.details as { ungrantable?: unknown; count?: unknown } | undefined;
  const raw = Array.isArray(details?.ungrantable) ? (details.ungrantable as unknown[]) : [];
  const keys = raw.filter((key): key is string => typeof key === 'string');

  return {
    labels: keys.map((key) => (isPermission(key) ? PERMISSION_META[key].label : key)),
    count: typeof details?.count === 'number' ? details.count : keys.length,
  };
}

/** `true` when the server refused because a clinic would be left with no owner. */
export function isLastOwnerRefusal(error: unknown): boolean {
  return isApiError(error) && error.code === 'STAFF_IS_LAST_OWNER';
}

/* -------------------------------------------------------------------------- */
/* Misc                                                                       */
/* -------------------------------------------------------------------------- */

/** Narrows the `string[]` the API returns to the registry's own union. */
export function toPermissions(values: readonly string[] | undefined): Permission[] {
  return sanitizePermissions(values ?? []);
}

/** Keys stored on a role that this build has no label for. Rendered verbatim. */
export function unlabelled(values: readonly string[] | undefined): string[] {
  return (values ?? []).filter((key) => !isPermission(key));
}

export { Permission };
